import { useAdvertiserStore } from '@stores/advertiser';
import type { TAdvertiserRetorno } from '@stores/advertiser/advertiser.types';

import type {
	TAnalyticsEvent,
	TAnalyticsDataLayer,
	TAnalyticsPageSection,
	TAnalyticsSiteSection,
	TAnalyticsUserSection,
	TAnalyticsTrackingCode,
	TAnalyticsUse,
} from '../analytics.types';

import {
	ADOBE_LAUNCH_HISTORY_KEY,
	DEFAULT_USE,
	MOBILE_BREAKPOINT_PX,
	SITE_DEFAULTS,
} from './analytics.constants';

export const sanitizeString = (value?: string | number | null): string | undefined => {
	if (value === null || value === undefined || value === '') return undefined;
	return String(value)
		.normalize('NFD')
		.replace(/[̀-ͯ](?=\B)|([^\w-](?=\B))/, '')
		.trim()
		.split(' ')
		.join('-')
		.toLowerCase()
		.replace(/[̀-ͯ]|([^\w-])/g, '');
};

export const getQueryStringParameter = (name: string, url?: string): string | undefined => {
	if (typeof window === 'undefined') return undefined;
	const target = url ?? window.location.href;
	const escaped = name.replace(/[[\]]/g, '\\$&');
	const regex = new RegExp(`[?&]${escaped}(=([^&#]*)|&|#|$)`);
	const results = regex.exec(target);
	if (!results) return undefined;
	return results[2] ?? '';
};

export const buildTier = (parts: string[]): Record<string, string> => {
	const obj: Record<string, string> = {};
	parts.forEach((part, index) => {
		obj[`pageNameTier${index + 1}`] = part;
	});
	return obj;
};

const buildPage = (event: TAnalyticsEvent): TAnalyticsPageSection => {
	const trackingCode: TAnalyticsTrackingCode = {
		idcmp: getQueryStringParameter('idcmp') || undefined,
		idcmpint: getQueryStringParameter('idcmpint') || undefined,
		icid: getQueryStringParameter('icid') || undefined,
		lkid: getQueryStringParameter('lkid') || undefined,
	};

	const tierParts = event.pageName.split('/').slice(event.tier * -1);

	return {
		pageName: event.pageName,
		pageType: event.pageType,
		flowType: event.flowType,
		trackingCode,
		...buildTier(tierParts),
		url: event.url ?? (typeof window !== 'undefined' ? window.location.href : ''),
	} as TAnalyticsPageSection;
};

const buildSite = (): TAnalyticsSiteSection => ({
	brandOrigin: getQueryStringParameter('origem') || SITE_DEFAULTS.defaultBrandOrigin,
	domain: (typeof window !== 'undefined' && window.document?.domain) || SITE_DEFAULTS.domain,
	country: SITE_DEFAULTS.country,
	server: SITE_DEFAULTS.server,
	environment: SITE_DEFAULTS.environment,
	subEnvironment: SITE_DEFAULTS.subEnvironment,
	tmsVersion: SITE_DEFAULTS.tmsVersion,
	clientType: SITE_DEFAULTS.clientType,
	platform:
		typeof window !== 'undefined' && window.screen?.width <= MOBILE_BREAKPOINT_PX
			? 'mobile'
			: 'desktop',
});

const formatBirthDate = (date?: string): string | undefined => {
	if (!date) return undefined;
	const iso = new Date(date).toISOString();
	return iso.replace(/-/g, '/').substring(0, 10);
};

const buildUser = (
	retorno: TAdvertiserRetorno,
	extra?: Record<string, unknown>,
): TAnalyticsUserSection => ({
	userID: String(retorno.Id ?? ''),
	loginStatus: '1',
	gender: retorno.Sexo === 'F' ? '2' : '1',
	birthDate: formatBirthDate(retorno.DataNascimento),
	location: {
		city: sanitizeString(retorno.Cidade),
		state: retorno.Uf?.toLowerCase(),
	},
	...(extra ?? {}),
});

export const buildDataLayer = (
	event: TAnalyticsEvent,
	retorno?: TAdvertiserRetorno,
): TAnalyticsDataLayer => {
	const use = event.use ?? DEFAULT_USE;
	const dataLayer: TAnalyticsDataLayer = {};

	use.forEach((key: TAnalyticsUse) => {
		switch (key) {
			case 'page':
				dataLayer.page = buildPage(event);
				break;
			case 'site':
				dataLayer.site = buildSite();
				break;
			case 'user':
				if (retorno) dataLayer.user = buildUser(retorno, event.user);
				break;
			default:
				break;
		}
	});

	return dataLayer;
};

export const getHistory = (): string[] => {
	try {
		const history = localStorage.getItem(ADOBE_LAUNCH_HISTORY_KEY);
		return history ? (JSON.parse(history) as string[]) : [];
	} catch {
		return [];
	}
};

const isDataLayerComplete = (
	dataLayer: TAnalyticsDataLayer,
	use: TAnalyticsUse[],
): boolean => use.every((key) => dataLayer[key] !== undefined);

export const dispatchAnalyticsEvent = (
	customEventName: string,
	event: TAnalyticsEvent,
): void => {
	const use = event.use ?? DEFAULT_USE;
	const retorno = useAdvertiserStore.getState().source?.Retorno;

	const dataLayer = buildDataLayer(event, retorno);

	if (!isDataLayerComplete(dataLayer, use)) return;

	if (!event.recurringEvent) {
		const history = getHistory();
		if (history.includes(event.pageName)) return;
		localStorage.setItem(
			ADOBE_LAUNCH_HISTORY_KEY,
			JSON.stringify([...history, event.pageName]),
		);
	}

	globalThis.objDataLayer = dataLayer;
	document.dispatchEvent(
		new CustomEvent(customEventName, { detail: dataLayer }),
	);
};
